/**
 * The reason prompt for a move to **Lost**.
 *
 * ===========================================================================
 * NOT IN FIGMA — AND NOT OPTIONAL EITHER
 * ===========================================================================
 * `02 Inquiry Pipeline` has a Lost column and a Lost pill, and no dialog anywhere for
 * getting into it. The API is unambiguous: `PATCH /inquiries/:id/stage` with
 * `stage:'lost'` and no reason is 422 `LOST_REASON_REQUIRED`, and the bulk endpoint
 * refuses `lost` outright because its body cannot carry one. So a board with a Lost
 * column and no prompt is a board with one drop target that always fails.
 *
 * This is therefore an authored component, and it is authored from parts that already
 * exist: the Radix Dialog the shell already depends on, the New Inquiry panel's field
 * geometry (`Input/Text`: `bg/subtle`, `border/default`, `radius/sm`, px-12,
 * `Body/14`), the `Label/12` section label, and the existing `Button` variants. No new
 * colour, no new radius, no new type size.
 *
 * `maxLength` is 200 because `lost_reason` is `VARCHAR(200)`. Truncating in the browser
 * beats discovering the limit as a 400 after the operator has typed a paragraph.
 */

import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '../../components/ui';
import { stageTabLabel } from '../../domain/stages';
import type { StageMove } from './stage-move';

export const LOST_REASON_MAX = 200;

/**
 * `null` closes the dialog; a move opens it.
 *
 * The keyed inner component is how the field is emptied between inquiries, instead of an
 * effect that resets it: `key={move.id}` makes React mount a fresh instance per move, so it
 * is structurally impossible for one customer's justification to appear pre-filled on
 * another's record.
 */
export function LostReasonDialog({
  move,
  busy,
  onSubmit,
  onCancel,
}: {
  move: StageMove | null;
  busy: boolean;
  onSubmit: (reason: string) => void;
  onCancel: () => void;
}): React.JSX.Element | null {
  if (move === null) return null;
  return (
    <LostReasonPrompt
      key={move.id}
      move={move}
      busy={busy}
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  );
}

function LostReasonPrompt({
  move,
  busy,
  onSubmit,
  onCancel,
}: {
  move: StageMove;
  busy: boolean;
  onSubmit: (reason: string) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [reason, setReason] = useState('');
  const valid = reason.trim() !== '';

  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[min(440px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 rounded-md border border-line bg-surface p-6 shadow-[var(--crf-shadow-overlay)]"
          onOpenAutoFocus={(event) => {
            // Focus the textarea, not the first button: the reason is the only reason
            // this dialog exists.
            event.preventDefault();
            const field = document.getElementById('lost-reason-field');
            if (field instanceof HTMLTextAreaElement) field.focus();
          }}
        >
          <Dialog.Title className="text-h3 text-fg">
            Why is this {stageTabLabel('lost').toLowerCase()}?
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-label text-fg-muted">
            {`${move.docNo} · moving from ${stageTabLabel(move.from)}. The reason is stored on the record and in the audit trail.`}
          </Dialog.Description>

          <form
            className="mt-4 flex flex-col gap-1.5"
            onSubmit={(event) => {
              event.preventDefault();
              if (valid && !busy) onSubmit(reason);
            }}
          >
            <label htmlFor="lost-reason-field" className="text-label font-medium text-fg">
              Reason
            </label>
            <textarea
              id="lost-reason-field"
              required
              rows={3}
              maxLength={LOST_REASON_MAX}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="e.g. Chose a competitor on price"
              className="w-full resize-y rounded-sm border border-line bg-subtle px-3 py-2 text-body text-fg outline-none placeholder:text-fg-muted"
            />
            <p className="tnum text-label text-fg-muted">
              {reason.length}/{LOST_REASON_MAX}
            </p>

            <div className="mt-3 flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={onCancel} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" disabled={!valid || busy}>
                {busy ? 'Saving…' : `Mark as ${stageTabLabel('lost')}`}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
